import type { FC, ReactNode } from "react";

import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import styled from "styled-components";

import { useGetAuthenticatedUserQuery } from "api/hooks/useGetAuthenticatedUserQuery";
import type { User } from "api/models/User";
import { useProfileContext } from "contexts/ProfileContext";
import { OooBanner } from "shared/atoms/OooBanner";
import { Spinner } from "shared/atoms/Spinner";
import { NavBar } from "shared/NavBar";
import { LOGIN_URL } from "utils/auth";
import { getRestHeaders, removeAuthCookies } from "utils/cookie";
import { initialRouteForProfile, isRouteAllowed, userIsPatient } from "utils/profile/profileHelper";

const ONBOARDING_PATH = "/onboarding";

const isOnboardingInProgress = ({ state }: User) => state === "in_onboarding";

interface ProtectedRouteProps {
  children: ReactNode;
  path: string;
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({ children, path }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const hasAuthCookies = getRestHeaders() !== null;
  const { profile, setProfile } = useProfileContext();

  const isOnboardingRoute = location.pathname.startsWith(ONBOARDING_PATH);

  // Always fetch profile
  const getAuthenticatedUserQuery = useGetAuthenticatedUserQuery({
    enabled: hasAuthCookies,
    onSuccess(response) {
      setProfile(response.data);
    },
  });

  if (!hasAuthCookies) {
    return <Navigate to={{ pathname: LOGIN_URL }} state={{ from: location }} />;
  }

  if (getAuthenticatedUserQuery.isError) {
    removeAuthCookies();
    return <Navigate to={{ pathname: LOGIN_URL }} state={{ from: location, error: getAuthenticatedUserQuery.error }} />;
  }

  if (profile) {
    if (userIsPatient(profile)) {
      removeAuthCookies();
      setProfile(undefined);
      return <Navigate to={{ pathname: LOGIN_URL }} state={{ from: location, error: t("errors.patient_login") }} />;
    }
    if (isOnboardingInProgress(profile) && !isOnboardingRoute) {
      return <Navigate to={{ pathname: ONBOARDING_PATH }} />;
    }
    if (!isRouteAllowed(profile, path)) {
      return <Navigate to={{ pathname: initialRouteForProfile(profile) }} state={{ from: location }} />;
    }
    return (
      <MainLayout>
        {!isOnboardingRoute && <NavBar />}
        <PageWrapper>
          <BannerWrapper>
            <OooBanner />
          </BannerWrapper>
          {children}
        </PageWrapper>
      </MainLayout>
    );
  }

  return (
    <Loading>
      <Spinner dataTestId="spinner" />
    </Loading>
  );
};

const Loading = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MainLayout = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
`;

const PageWrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  flex: 1;
`;

const BannerWrapper = styled.div`
  width: 100%;
`;
